import styled from 'styled-components';
import { spacing } from '_utils/props';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { buttonReset, hideOnPrint, mq, heading_xl, heading_l, heading_m, heading_s, heading_xs, svg } from '_utils/styles/';
import ContentRichText from '../ContentRichText';

export const Item = styled.li`
  overflow: auto;
  position: relative;
  border: ${({ theme }) => theme.accordion.border};

  &.hover-active {
    border: ${({ theme }) => theme.accordion.borderHover};
  }

  &:first-child {
    border-top-left-radius: ${({ theme }) => theme.accordion.borderRadius}px;
    border-top-right-radius: ${({ theme }) => theme.accordion.borderRadius}px;
  }

  &:last-child {
    border-bottom-left-radius: ${({ theme }) => theme.accordion.borderRadius}px;
    border-bottom-right-radius: ${({ theme }) => theme.accordion.borderRadius}px;
  }

  &:not(:last-child) {
    border-bottom: none;
  }

  /* turn on border top of next sibling so we get a complete border on hover */
  &:not(:last-child).hover-active + & {
    border-top: ${({ theme }) => theme.accordion.borderHover};
  }

  &:focus-within {
    border: 2px solid ${({ theme }) => theme.colors.element.focused};
  }

  /* &.edit-mode {
    padding-bottom: 16px;
  } */
`;

export const Button = styled.button`
  ${buttonReset()}
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  position: relative;
  outline: 0;

  ${({ theme }) => spacing({ theme, $px: 3 })}

  /* when button has focus */
  body:not(.safe-focus-removal) &:focus {
    &:before {
      content: '';
      position: absolute;
      left: -4px;
      top: -4px;
      width: calc(100% + 8px);
      height: calc(100% + 8px);
    }
  }

  ${Item}:first-child &:before {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  ${Item}:last-child &:before {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  ${Item}:last-child.expanded &:before {
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
  }
`;

export const Heading = styled(Text)`
  h2& {
    ${heading_xl}
  }

  h3& {
    ${heading_l}
  }

  h4& {
    ${heading_m}
  }

  h5& {
    ${heading_s}
  }

  h6& {
    ${heading_xs}
  }

  h2&,
  h3&,
  h4& {
    ${({ theme }) => spacing({ theme, $py: 3 })}
  }

  h5&,
  h6& {
    ${({ theme }) => spacing({ theme, $py: 2 })}
  }

  display: block;
  flex: 1 1 auto;

  ${Item}.expanded & {
    color: ${({ theme }) => theme.accordion.action};
  }

  @media (hover: hover) {
    ${Button}:hover & {
      color: ${({ theme }) => theme.accordion.actionHover};
    }
  }

  ${Item}.edit-mode && {
    color: inherit;
  }
`;

export const Content = styled.div<{ $isExpanding: boolean }>`
  overflow: ${(props) => props.$isExpanding? '': 'hidden'};

  transition-property: height, padding;
  transition-duration: ${({ theme }) => theme.motion.timing.fast};
  transition-timing-function: ${({ theme }) => theme.motion.ease};
  ${({ theme }) => spacing({ theme, $pl: 3 })}

  padding-right: 56px;

  ${mq('md')} {
    padding-right: 72px;
  }

  @media print {
    height: auto !important;
  }
`;

export const RichText = styled(ContentRichText)`
  ${({ theme }) =>
    spacing({
      theme,
      $pb: 3
    })}

  > :last-child {
    margin-bottom: 0;
  }
`;

export const ChevronWrapper = styled.span`
  ${svg(24, 24)}
  ${({ theme }) => spacing({ theme, $ml: 2 })}

  width: 24px;

  ${mq('md')} {
    width: 32px;
    h5 ~ &,
    h6 ~ & {
      width: 24px;
    }
  }

  flex: none;

  @media (hover: hover) {
    ${Button}:hover & {
      path {
        fill: ${({ theme }) => theme.accordion.actionHover};
      }
    }
  }

  path {
    fill: ${({ theme }) => theme.accordion.action};
  }

  transition-property: transform;
  transition-duration: ${({ theme }) => theme.motion.timing.fast};
  transition-timing-function: ${({ theme }) => theme.motion.ease};

  ${Item}.expanded & {
    transform: rotate(180deg);
  }

  ${Item}.edit-mode & {
    transform: rotate(0);
  }
  ${hideOnPrint}
`;
